import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { ConfirmationModal, TextField } from 'seagull';
import { RichTextEditor, extractPureTextFromRichText } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { ServerError } from '~/infra/ErrorResponse';
import { useLegalTerms } from '../../../hooks/reactQuery/queries/useLegalTerms';
import styles from './CreateDocumentModal.module.scss';
const MAX_NAME_LENGTH = 80;
const MAX_CONTENT_LENGTH = 100000;
export const { CreateDocumentModalTID } = getTestIds();
export const CreateDocumentModal = (props)=>{
    const { onConfirm, dismissModal } = props;
    const { legalTerms } = useLegalTerms();
    const [name, setName] = useState('');
    const [content, setContent] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(undefined);
    const existingNames = useMemo(()=>new Set(legalTerms.map((term)=>term.name.toLowerCase())), [
        legalTerms
    ]);
    const isDuplicateName = existingNames.has(name.trim().toLowerCase());
    const isNameTooLong = name.length > MAX_NAME_LENGTH;
    const contentText = content ? extractPureTextFromRichText(content) : '';
    const isContentTooLong = contentText.length > MAX_CONTENT_LENGTH;
    const isContentEmpty = !contentText.trim();
    const isNameEmpty = !name.trim();
    const hasNameError = !!error || isDuplicateName || isNameTooLong;
    const hasContentError = isContentTooLong;
    const hasError = hasNameError || hasContentError;
    const nameHelperText = useMemo(()=>{
        if (isDuplicateName || error === ServerError.LegalTermsNameAlreadyExists) {
            return 'Document name already exists. Please use a different one.';
        } else if (isNameTooLong) {
            return 'Document name can be up to 80 characters';
        }
    }, [
        isDuplicateName,
        isNameTooLong,
        error
    ]);
    const contentHelperText = isContentTooLong ? 'Document content can be up to 100,000 characters' : undefined;
    const handleCreate = async ()=>{
        if (!hasError && name.trim()) {
            setIsSaving(true);
            const result = await onConfirm(name.trim(), content);
            if (result.success) {
                dismissModal(result.data);
            } else if (result.error === ServerError.LegalTermsNameAlreadyExists) {
                setError(result.error);
            }
            setIsSaving(false);
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: "Create document",
        width: "800px",
        height: "fit-content",
        testId: CreateDocumentModalTID.Root(),
        textAlignCenter: false,
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.contentWrapper,
            children: [
                /*#__PURE__*/ _jsx(TextField, {
                    collapseHelperText: true,
                    testId: CreateDocumentModalTID.NameInput(),
                    label: "Document name",
                    value: name,
                    onChange: (e)=>{
                        setName(e.target.value);
                        setError(undefined);
                    },
                    error: hasNameError,
                    helperText: nameHelperText,
                    helperTextReserveSpace: true,
                    width: "100%",
                    autoFocus: true
                }),
                /*#__PURE__*/ _jsx(RichTextEditor, {
                    testId: CreateDocumentModalTID.ContentEditor(),
                    editorHeight: 300,
                    error: hasContentError,
                    helperText: contentHelperText,
                    onChange: (value)=>setContent(value)
                })
            ]
        }),
        primaryButtonLabel: "Create",
        secondaryButtonLabel: "Cancel",
        primaryButtonDisabled: hasError || isNameEmpty || isContentEmpty,
        primaryButtonLoading: isSaving,
        onPrimaryButtonClick: handleCreate,
        onSecondaryButtonClick: ()=>dismissModal()
    });
};
